/**
 * The hand-over-the-pool picker (ISSUE-817 R4 and R5).
 *
 * A pool always has exactly one Commissioner, and they cannot be removed or
 * demoted, only hand the role to somebody else. That makes this one dialog the
 * only way the role ever moves on web. It is reached two ways, and `thenLeave`
 * is what tells them apart:
 *
 *   - the Commissioner's own `Leave pool` row in the members overflow menu
 *     passes `thenLeave`, and the transfer is followed by the leave (R4),
 *   - the Settings Danger zone does not, and the old Commissioner stays in the
 *     pool as an Admin (R5). `admin`, not `member`: `transferOwnership`
 *     demotes them in the same transaction that promotes the successor.
 *
 * Per owner answer Q184 the role goes to ANY current Member or Admin in one
 * step, so there is no promote-then-transfer path and no prior-Admin filter.
 *
 * The sole-member state carries no shut-the-pool-down control, and that is
 * still right, but the reason changed. It used to be that no such endpoint
 * existed anywhere; `pool-api` now has `POST /pool/archive` and the Settings
 * Danger zone owns it (`poolSettings/ClosePoolButton.tsx`). Closing a pool is
 * not a way of handing it over, so it does not belong in this picker: it
 * belongs one level up, next to the other thing a Commissioner can do to a
 * pool they no longer want. The empty state here stays an empty state.
 *
 * `apps/mobile`'s hand-over screen still says there is no endpoint at all.
 * That comment is now stale and its screen still has no way out for a sole
 * Commissioner; bringing mobile across is its own card.
 */
import {
  useMemberList,
  useOwnershipTransfer,
  usePoolLeave,
} from "@sportsmet/pool-sdk/private";
import { useProfileGet } from "@sportsmet/profile-sdk/private";
import {
  Button,
  confirmDestructive,
  Dialog,
  InlineBanner,
  Skeleton,
} from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import { handOverConfirm } from "./memberActionConfirm";

export const HAND_OVER_DIALOG_NAME = "hand-over";
export const HAND_OVER_TITLE = "Hand over the pool";
export const HAND_OVER_CANCEL_LABEL = "Cancel";
export const HAND_OVER_EMPTY_TITLE = "Nobody to hand the pool to";
export const HAND_OVER_EMPTY_BODY = "You're the only one left in this pool.";
export const HAND_OVER_FAILED_MESSAGE = "We could not load these members.";
export const HAND_OVER_RETRY_LABEL = "Try again";
export const HAND_OVER_LOADING_LABEL = "Loading members";

export type typeHandOverPoolDialogProps = {
  open: boolean;
  poolId: string;
  /**
   * True only for the Commissioner's own `Leave pool`. The transfer is followed
   * by `/pool/leave`; false means the transfer happens alone and the old
   * Commissioner keeps their membership.
   */
  thenLeave: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Called after the whole flow lands. `thenLeave` decides what the caller does
   * with it: the members pane navigates away, the Settings card just closes.
   */
  onDone: () => void;
};

export const HandOverPoolDialog = ({
  open,
  poolId,
  thenLeave,
  onOpenChange,
  onDone,
}: typeHandOverPoolDialogProps): ReactNode => {
  // The roster is read only while the picker is open. A screen that fetched
  // every member just to render one button would pay for a list nobody asked
  // for.
  const members = useMemberList(
    { poolId, statuses: ["active"], limit: 200 },
    { enabled: open },
  );
  const viewer = useProfileGet({});
  const transfer = useOwnershipTransfer();
  const leave = usePoolLeave();
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  const viewerUserId = viewer.data?.data?.profile?.accountId ?? "";
  // Both filters mirror what /ownership/transfer refuses: a target who is not
  // active comes back CONFLICT "targetNotActive", and the viewer themselves
  // comes back CONFLICT "self".
  const eligible = (members.data?.data?.items ?? []).filter(
    (item) => item.status === "active" && item.userId !== viewerUserId,
  );

  const isLoading = open && (members.isLoading || viewer.isLoading);
  // A ROSTER WE CANNOT FILTER IS A FAILURE, NOT AN EMPTY VIEWER ID.
  // `viewerUserId` falls back to "" when `/profile/get` has not answered, and
  // "" matches nobody, so the filter above silently keeps the Commissioner in
  // their own picker, a row that can only ever come back CONFLICT "self".
  // Found by the ISSUE-817 browser pass, where `/profile/get` failed and the
  // picker listed the viewer. Treated as a failed read rather than shown.
  const hasFailed =
    open &&
    !isLoading &&
    (members.isError ||
      Boolean(members.data?.error) ||
      viewer.isError ||
      viewerUserId === "");

  async function handOver(member: {
    userId: string;
    displayName: string;
  }): Promise<void> {
    // Neither endpoint takes a request id, so this guard is the whole defense
    // against a second press transferring the pool twice.
    if (busyUserId !== null) return;

    const isConfirmed = await confirmDestructive(
      handOverConfirm({ targetDisplayName: member.displayName, thenLeave }),
    );
    if (!isConfirmed) return;

    setBusyUserId(member.userId);
    try {
      const transferred = await transfer.mutateAsync({
        poolId,
        targetUserId: member.userId,
      });
      if (showApiError(transferred)) return;

      // R5: the Settings path hands the pool over and stays. Returning HERE,
      // before any leave, is the whole of that requirement.
      if (!thenLeave) {
        onDone();
        return;
      }

      const left = await leave.mutateAsync({ poolId });
      // The transfer already succeeded, so they are no longer the
      // Commissioner. A failed leave is reported and they stay put; it is
      // never retried and never undone.
      if (showApiError(left)) return;
      onDone();
    } catch {
      showOfflineError();
    } finally {
      setBusyUserId(null);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={HAND_OVER_TITLE}
      autoFocus={false}
      footer={
        <Button
          variant="secondary"
          onClick={() => {
            onOpenChange(false);
          }}
        >
          {HAND_OVER_CANCEL_LABEL}
        </Button>
      }
    >
      {isLoading ? (
        <div data-testid="hand-over-loading">
          <Skeleton shape="title" label={HAND_OVER_LOADING_LABEL} />
        </div>
      ) : hasFailed ? (
        <div data-testid="hand-over-failed">
          <InlineBanner
            variant="warning"
            message={HAND_OVER_FAILED_MESSAGE}
            action={{
              label: HAND_OVER_RETRY_LABEL,
              onAction: () => {
                void members.refetch();
                void viewer.refetch();
              },
            }}
          />
        </div>
      ) : eligible.length === 0 ? (
        <div className="flex flex-col gap-1" data-testid="hand-over-empty">
          <p className="text-body text-ink">{HAND_OVER_EMPTY_TITLE}</p>
          <p className="text-caption text-ink-secondary">
            {HAND_OVER_EMPTY_BODY}
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {eligible.map((member) => (
            <li key={member.userId}>
              <Button
                fullWidth
                size="md"
                variant="secondary"
                data-testid={`hand-over-${member.userId}`}
                loading={busyUserId === member.userId}
                disabled={busyUserId !== null}
                onClick={() => {
                  void handOver({
                    userId: member.userId,
                    displayName: member.displayName,
                  });
                }}
              >
                {member.displayName}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
};
